import React, { useEffect, useContext, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { useObserver } from 'mobx-react-lite';
import { GlobalHotKeys } from 'react-hotkeys';

import QuickDrawerHeader from '../../../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../../../_shared/QuickDrawer';
import AddressInput from '../../../../_shared/AddressInput';

import TenantCreateUpdateStore from '../../../../../../stores/TenantCreateUpdateStore';
import AuthStore from '../../../../../../stores/AuthStore';
import QuickDrawerStore from '../../../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../../../constants/errorMessages';
import * as fn from '../../../../../../utilities/_functions';
import * as sys from '../../../../../../utilities/systemHelper';
import * as oh from '../../../../../../utilities/operationHelper';

import './UpdateTenantAddress.scss';

function UpdateTenantAddress(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const focusTimer = useRef(null);
    const addressRef = useRef(null);
    const tenant = useContext(TenantCreateUpdateStore);
    const auth = useContext(AuthStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const cachedAddressRef = useRef(null);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100)

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    const handleAddressChange = (data) => {
        tenant.newAddress = data;
        tenant.hasAddressUnsavedChanges = true;
    }

    const handleCancel = () => {
        if (cachedAddressRef.current)
            tenant.data.address = cachedAddressRef.current;

        tenant.clearNewAddress();
        if (fn.isFunction(props.onCancel)) {
            if (tenant.hasAddressUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current) && addressRef.current.validate()) {
            if (tenant.hasAddressUnsavedChanges) {
                if (!tenant.data.address) {
                    return;
                }
                const updated = tenant.hasAddressUnsavedChanges;

                if (tenant.data.id) {
                    // if this is an existing tenant, then call backend to update address. Otherwise, leave it to the "Create" to create the address
                    tenant.updateAddressInformation()
                        .then(data => {
                            if (isMounted.current) {
                                if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                                    props.onSuccess(event, { updated: updated, data: data });
                                }
                            }
                        });
                }
                else {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: updated, data: null });
                    }
                }
            } else {
                if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                    props.onSuccess(event, { updated: false, data: null });
                }
            }
        }
    }

    const renderTenant = () => {
        return <div
            className='profile-wrapper'
        >
            <div className='profile'>
                <span
                    className={`d-flex bg-color${tenant.data.color}-500 fw-500`}
                    title={tenant.data.legalName}
                >
                    {tenant.data.legalName}
                </span>
            </div>
            <div className='description flex-1'>
                {
                    tenant.data.emailAddress ?
                        <div className='info'>
                            <a
                                href={`mailto:${tenant.data.emailAddress}`}
                            >{tenant.data.emailAddress}
                            </a>
                        </div> : null
                }
                {
                    tenant.data.phoneNumber ?
                        <div className='info'>
                            <a
                                href={`tel:${tenant.data.phoneNumber}`}
                            >{sys.getFormattedPhoneNumber(tenant.data.phoneNumber)}
                            </a>
                        </div> : null
                }
            </div>
        </div>
    }


    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset disabled={tenant.isSaving}>
                <div className='update-tenant-address-container quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('tenant', 'address')}
                        action='Update Tenant'
                        category='Address'
                        className='tenants'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        {
                            tenant.isReady && tenant.data ?
                                <FadeIn>
                                    <div className='body-content'>
                                        <section className='tenant'>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-0'>
                                                        <label><small>Tenant</small></label>
                                                        {renderTenant()}
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                        <section>
                                            <AddressInput
                                                ref={addressRef}
                                                address={(tenant.data && tenant.data.address ? tenant.data.address : {})}
                                                label={'Address'}
                                                required={true}
                                                defaultCountryCode={(auth.currentUser && auth.currentTenant && auth.currentTenant.address ? auth.currentTenant.address.countryCode : null)}
                                                onChange={handleAddressChange}
                                            />
                                        </section>
                                    </div>
                                </FadeIn> : renderQuickDrawerLoading()
                        }
                    </div>
                    <div className='quick-drawer-action pl-3'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>)
}

export default UpdateTenantAddress;
